import { Typography } from "@material-tailwind/react";
import { Table } from "../../../components/table/Table";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import { t } from "../../../i18n";
import { toast } from "react-hot-toast";
import { api } from "../../../services/api/api";
import ServiceForm, { ServiceFormValidator } from "../../../components/data/service/ServiceForm";
import EditCreateDrawer from "../../../components/edit-create-drawer/EditCreateDrawer";
import { validator } from "../../../services/form-validator/form-validator";
import { isOdooConnected } from "../../../services/odoo/odoo";

export default function ServicesTable({
    services,
    selectedHead,
    orderDesc,
    onHeadClick,
    onServiceUpdated
}: {
    services: EasyTech.Service[],
    selectedHead?: string,
    orderDesc?: boolean,
    onHeadClick?: (head: string, orderDesc: boolean) => void,
    onServiceUpdated?: () => void
}) {
    const isNotOdooConnected = !isOdooConnected();

    const TABLE_HEAD = [
        { label: t("common:name"), orderKey: "label", width: "30%" },
        { label: t("piece:ref"), orderKey: "ref", width: "20%" },
        { label: t("common:type"), orderKey: "type", width: "20%" },
        { label: t("piece:category"), orderKey: "category.label", width: "15%" },
        { label: t("common:sellingPrice"), orderKey: "price", width: "15%" },
        ...(isNotOdooConnected ? [{ label: t("common:actions"), orderKey: null, width: "5%" }] : [])
    ];

    const handleServiceEdit = (service: EasyTech.Service): Promise<void> => {
        return new Promise((resolve, reject) => {
            const serviceToSave = {
                ...service,
                price: typeof service.price === "string" ? parseFloat(service.price) : service.price
            };

            if (!serviceToSave.category?.id) {
                delete serviceToSave.category;
            }

            if (validator.isFormValid(serviceToSave, ServiceFormValidator)) {
                api.put(`users/services/${serviceToSave.id}`, serviceToSave)
                    .then(() => {
                        toast.success(t("common:success"));
                        if (onServiceUpdated) {
                            onServiceUpdated();
                        }
                        resolve();
                    })
                    .catch((err) => {
                        console.error(err);
                        toast.error(t("common:error"));
                        reject(err);
                    });
            } else {
                const fieldNames = {
                    label: t("common:label"),
                    ref: t("common:ref:label"),
                    category: t("common:category"),
                    price: t("common:price"),
                    type: t("common:type")
                };

                const errorMessage = validator.getFormErrorMessage(serviceToSave, ServiceFormValidator, fieldNames, t);
                toast.error(errorMessage);
                reject();
            }
        });
    };

    return (
        <Table
            tableHead={TABLE_HEAD}
            emptyTable={!services || services.length === 0 ? "No Services" : false}
            selectedHead={selectedHead}
            orderDesc={orderDesc}
            onHeadClick={onHeadClick}
            enableRowLinks
        >
            {services.map((service, index) => {
                const isLast = index === services.length - 1;
                const rowClass = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";

                return (
                    <tr
                        key={`${service.id}_${index}`}
                        className={rowClass}
                        data-href={`/services/${service.id}`}
                    >
                        <td className={rowClass}>
                            <Typography variant="small" color="blue-gray" className="et-cell-wrap">
                                {service.label || " "}
                            </Typography>
                        </td>

                        <td className={rowClass}>
                            <Typography variant="small" color="blue-gray" className="et-cell-wrap">
                                {service.ref || " "}
                            </Typography>
                        </td>

                        <td className={rowClass}>
                            <OverflowTooltip content={service.type || ""} className="et-cell-truncate">
                                <Typography variant="small" color="blue-gray">
                                    {service.type || " "}
                                </Typography>
                            </OverflowTooltip>
                        </td>

                        <td className={rowClass}>
                            <OverflowTooltip content={service.category?.label || ""} className="et-cell-truncate">
                                <Typography variant="small" color="blue-gray">
                                    {service.category?.label || " "}
                                </Typography>
                            </OverflowTooltip>
                        </td>

                        <td className={rowClass}>
                            {(() => {
                                const priceLabel = typeof service.price === "number" ? service.price.toFixed(2) : " ";
                                return (
                                    <OverflowTooltip content={priceLabel} className="et-cell-truncate">
                                        <Typography variant="small" color="blue-gray">
                                            {priceLabel}
                                        </Typography>
                                    </OverflowTooltip>
                                );
                            })()}
                        </td>

                        {isNotOdooConnected && (
                            <td className={rowClass} data-no-link>
                                <EditCreateDrawer<EasyTech.Service>
                                    entityName="Service"
                                    data={service}
                                    FormComponent={ServiceForm}
                                    onSubmit={handleServiceEdit}
                                    isNotCreatable
                                />
                            </td>
                        )}
                    </tr>
                );
            })}
        </Table>
    );
}
